import { useRef, useEffect, useState } from "react";
import { X, FileDown } from "lucide-react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";
import { obtenerInfoUsuario } from "../api/api-autenticacion";

const LABEL_BG = "#d4b896";
const BORDER = "#333";

/**
 * Modal that shows the route sheet (hoja de ruta) matching the SECOBOL template and exports as PDF (letter).
 */
export default function ModalHojaDeRutaTiemposEntrega({ visible = false, onClose, items = [], t, selectedDate, infoConductor }) {

    const [infoUsuario, setInfoUsuario] = useState(null);

    const contentRef = useRef(null);

    useEffect(() => {
        if (visible === false) return;
        obtenerDatos();
    }, [visible]);

    const obtenerDatos = async () => {
        try {
            // console.log('obtenerDatos', items);
            const response = await obtenerInfoUsuario();
            // console.log('response', response);
            setInfoUsuario(response);
        } catch (err) {
            console.log('obtenerDatos err', err);
        }
    };

    const handleExportPdf = async () => {
        if (!contentRef.current) return;
        try {
            const canvas = await html2canvas(contentRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: "#ffffff",
            });
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "letter");
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();
            const margin = 8;
            const w = pageW - margin * 2;
            const imgH = (canvas.height * w) / canvas.width;
            let heightLeft = imgH;
            let position = margin;
            pdf.addImage(imgData, "PNG", margin, position, w, imgH);
            heightLeft -= pageH;
            while (heightLeft > 0) {
                position = heightLeft - imgH + margin;
                pdf.addPage("p", "mm", "letter");
                pdf.addImage(imgData, "PNG", margin, position, w, imgH);
                heightLeft -= pageH;
            }
            pdf.save(`hoja-de-ruta-${moment().format("YYYY-MM-DD-HHmm")}.pdf`);
        } catch (err) {
            console.warn("Export PDF err", err);
        }
    };

    if (!visible) return null;

    const placa = items?.[0]?.labelPlaca ?? items?.[0]?.valuePlaca ?? "";
    const fechaDoc = moment().format("DD-MM-YYYY");
    const rgLog = `RG-LOG-01`;
    const numeroRgLog = moment(selectedDate).format("YYYYMMDD");

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-4xl max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {t("label_hoja_de_ruta")}
                    </h3>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleExportPdf}
                            className="flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium"
                        >
                            <FileDown className="h-4 w-4" />
                            {t("label_exportar_pdf")}
                        </button>
                        <button type="button" onClick={onClose} className="p-1 text-gray-600 hover:text-gray-900 rounded" aria-label={t("label_cerrar")}>
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-auto p-4">
                    <div
                        ref={contentRef}
                        className="bg-white text-black"
                        style={{
                            // width: "215.9mm",
                            // minWidth: "215.9mm",
                            fontFamily: "sans-serif",
                            fontSize: "11px"
                        }}
                    >
                        {/* Header: logo area + title */}
                        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "12px", borderBottom: `1px solid ${BORDER}` }}>
                            <div style={{ fontWeight: "bold", fontSize: "18px", color: "#444", marginRight: "16px" }}>
                                {infoUsuario?.nombreEmpresa ?? ""}
                            </div>
                            <div style={{ flex: 1, textAlign: "center" }}>
                                <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "2px" }}>{t("label_hoja_de_ruta_y_carga")}</div>
                                <div style={{ fontSize: "11px", color: "#333" }}>({t("label_control_despacho_y_transporte")})</div>
                            </div>
                        </div>

                        {/* General information table */}
                        <table style={{ width: "100%", borderCollapse: "collapse", border: `1px solid ${BORDER}`, marginBottom: "12px" }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", width: "18%", backgroundColor: LABEL_BG, fontWeight: "bold" }}>{t("title_empresa")}:</td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", width: "32%" }}>{infoUsuario?.nombreEmpresa ?? ""}</td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", width: "18%", backgroundColor: LABEL_BG, fontWeight: "bold" }}>{t("title_nombre_del_chofer")}:</td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", width: "32%" }}>{infoConductor?.nombreConductor ?? ""}</td>
                                </tr>
                                <tr>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", backgroundColor: LABEL_BG, fontWeight: "bold" }}>{t("title_fecha")}:</td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px" }}>{fechaDoc}</td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", backgroundColor: LABEL_BG, fontWeight: "bold" }}>{t("title_placa_del_camion")}:</td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", fontWeight: "bold" }}>{placa}</td>
                                </tr>
                                <tr>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", backgroundColor: LABEL_BG, fontWeight: "bold" }}>{t("title_n_documento")}:</td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px" }}>{rgLog}</td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", backgroundColor: LABEL_BG, fontWeight: "bold" }}>{t("title_hora_de_salida")}:</td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px" }}></td>
                                </tr>
                                <tr>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px" }}></td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px" }}>{numeroRgLog}</td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", backgroundColor: LABEL_BG, fontWeight: "bold" }}>{t("title_hora_de_retorno")}:</td>
                                    <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px" }}></td>
                                </tr>
                            </tbody>
                        </table>

                        {/* Route details table */}
                        <table style={{ width: "100%", borderCollapse: "collapse", border: `1px solid ${BORDER}`, marginBottom: "12px" }}>
                            <thead>
                                <tr>
                                    <th style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", fontWeight: "bold", textAlign: "center", width: "6%" }}>N°</th>
                                    <th style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", fontWeight: "bold", textAlign: "center", width: "14%" }}>{t("title_fecha_emision")}</th>
                                    <th style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", fontWeight: "bold", textAlign: "center", width: "12%" }}>{t("title_n_venta")}</th>
                                    <th style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", fontWeight: "bold", textAlign: "center", width: "12%" }}>{t("title_n_factura")}</th>
                                    <th style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", fontWeight: "bold", textAlign: "left", width: "44%" }}>{t("title_cliente")}</th>
                                    <th style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", fontWeight: "bold", textAlign: "center", width: "12%" }}>{t("title_tiempo")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} style={{ border: `1px solid ${BORDER}`, padding: "8px", textAlign: "center", color: "#666" }}>
                                            {t("label_sin_datos")}
                                        </td>
                                    </tr>
                                ) : (
                                    items.map((row, index) => (
                                        <tr key={row.idDetallePedido || index}>
                                            <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", textAlign: "center" }}>{index + 1}</td>
                                            <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", textAlign: "center" }}>
                                                {row.fechaFactura ? moment(row.fechaFactura).format("DD/MM/YYYY") : row.fechaEntregaProgramada ? moment(row.fechaEntregaProgramada).format("DD/MM/YYYY") : ""}
                                            </td>
                                            <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", textAlign: "center" }}>{row.numeroDocumento ?? ""}</td>
                                            <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", textAlign: "center" }}>{row.numeroFactura ?? "0"}</td>
                                            <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px" }}>{row.cliente ?? ""}</td>
                                            <td style={{ border: `1px solid ${BORDER}`, padding: "6px 8px", textAlign: "center" }}>{row.tiempoAtencionCliente ?? "--"}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="px-4 py-3 border-t border-gray-200 shrink-0">
                    <button type="button" onClick={onClose} className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-sm font-medium">
                        {t("label_cerrar")}
                    </button>
                </div>
            </div>
        </div>
    );
}
